import { createContext, type ReactNode, useContext, useMemo } from "react";

import type { ListParams } from "./build-list-params";
import type { FilterFieldsConfig } from "./types";

/**
 * One count read, as react-query options: the list's own query key and fetch
 * for `params`, and how to read the row count out of its response.
 */
export interface OptionCountQuery<TData = unknown> {
  queryKey: readonly unknown[];
  queryFn: () => Promise<TData>;
  select: (data: TData) => number | undefined;
  /** `false` while the screen cannot count yet (its scope id is missing). */
  enabled?: boolean;
}

interface OptionCounts {
  fields: FilterFieldsConfig;
  countQueryOf: (params: ListParams) => OptionCountQuery;
}

const OptionCountsContext = createContext<OptionCounts | null>(null);

/**
 * Turns on per-option row counts for the filter chips beneath it — for the
 * fields that set `countOptions`. The screen says how its list counts rows for
 * a set of list params (`countQueryOf`): a `countOnly` read, a one-row
 * `withCount` read, or whatever its endpoint offers. The chips decide which
 * params each option needs (`optionCountParams`).
 *
 * Sits inside the screen's `FilterUrlKeysProvider`, so counts read the same
 * URL keys as the list.
 */
export function OptionCountsProvider<TData>({
  fields,
  countQueryOf,
  children,
}: {
  fields: FilterFieldsConfig;
  countQueryOf: (params: ListParams) => OptionCountQuery<TData>;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      fields,
      // Erased for storage: each query's `select` only ever receives its own
      // `queryFn`'s result, so TData stays consistent per query.
      countQueryOf: countQueryOf as (params: ListParams) => OptionCountQuery,
    }),
    [fields, countQueryOf],
  );
  return (
    <OptionCountsContext.Provider value={value}>
      {children}
    </OptionCountsContext.Provider>
  );
}

export function useOptionCountsContext(): OptionCounts | null {
  return useContext(OptionCountsContext);
}
